import * as React from "react"
import { CheckIcon, MinusIcon } from "lucide-react"
import { Checkbox as CheckboxPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

/**
 * Checkbox, in the current shadcn (base-nova) shape.
 *
 * The previous one wore a soft `shadow-xs` and a light border on a tinted dark
 * fill — a bevelled, skeuomorphic square that read as a decade-old control. The
 * modern one is flat: a crisp 1px hairline on the page's own background,
 * filling solid with the accent when checked, and a single focus ring that does
 * not also animate the shadow.
 *
 * Kept on Radix deliberately. The upstream docs have moved to `@base-ui/react`,
 * but this is one of dozens of primitives here and swapping the library under
 * only one of them would leave two behaviours, two focus models and two sets of
 * data-attributes in the same app. The LOOK is what was wrong, and the look is
 * entirely in these classes.
 *
 * `indeterminate` is styled as well as checked — a table header with a partial
 * selection is the one place a checkbox most needs to be legible, and a dash
 * that inherits the checked fill says "some" far faster than an empty box.
 */
function Checkbox({
  className,
  ...props
}: React.ComponentProps<typeof CheckboxPrimitive.Root>) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        "peer size-4 shrink-0 rounded-[0.25rem] border border-input bg-background outline-none",
        "transition-colors duration-100",
        "hover:border-ring/60",
        "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
        "data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
        "data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground",
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="grid place-content-center text-current"
      >
        {props.checked === "indeterminate" ? (
          <MinusIcon className="size-3 stroke-[3]" />
        ) : (
          <CheckIcon className="size-3 stroke-[3]" />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
}

export { Checkbox }
